import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getEquipmentIconFromType } from '@utils/equipmentIcons';
import { nameInitials } from '@utils/nameInitials';
import { professionChipStyle } from '@utils/professionColor';
import { tradeLabel } from '@utils/tradeLabel';
import { ChevronRightIcon } from '@utils/icons';
import type {
  EquipmentPresence,
  ManagerPresence,
  UtilityTypeChip,
} from '@store/selectors/monitor.selectors';
import styles from './floorDrawer.module.css';

// ---------- Panels ----------

interface IPersonnelPanelProps {
  managers: ManagerPresence[];
  professions: {
    id: string;
    tradeId: string | undefined;
    tradeGroup: string | undefined;
    color: string;
    tagIds: string[];
  }[];
  equipment: EquipmentPresence[];
  utilities: UtilityTypeChip[];
  emptyText: string;
}

export function PersonnelPanel({
  managers,
  professions,
  equipment,
  utilities,
  emptyText,
}: IPersonnelPanelProps) {
  const { t } = useTranslation();
  if (
    managers.length === 0 &&
    professions.length === 0 &&
    equipment.length === 0 &&
    utilities.length === 0
  ) {
    return <EmptyMsg text={emptyText} />;
  }
  return (
    <div className={styles.sections}>
      {/* Managers — initials avatar + name, mirroring the floor-plan manager
          pins and the building-view popover. */}
      {managers.length > 0 && (
        <CollapsibleSection title={t('monitor.workforce.sections.managers')}>
          <ul className={styles.list}>
            {managers.map((mgr) => (
              <li key={`mgr-${mgr.id}`} className={styles.row}>
                <span
                  className={styles.managerAvatar}
                  style={professionChipStyle(mgr.backgroundColor)}
                >
                  {nameInitials(mgr.name)}
                </span>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>{mgr.name}</span>
                </div>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {professions.length > 0 && (
        <CollapsibleSection title={t('monitor.workforce.sections.professions')}>
          <ul className={styles.list}>
            {professions.map((row) => {
              const label = tradeLabel(t, row.tradeId) || row.tradeGroup || 'Unknown';
              return (
                <li key={row.id} className={styles.row}>
                  <span className={styles.rowStripe} style={{ background: row.color }} />
                  <div className={styles.rowMain}>
                    <span className={styles.rowTitle}>{label}</span>
                    <span className={styles.rowSub}>
                      {row.tagIds.slice(0, 4).join(' · ')}
                      {row.tagIds.length > 4 ? ` · +${row.tagIds.length - 4}` : ''}
                    </span>
                  </div>
                  <span className={styles.rowMeta}>{row.tagIds.length}</span>
                </li>
              );
            })}
          </ul>
        </CollapsibleSection>
      )}

      {/* Equipment + utilities — one row per type, count on the right; a live
          dot marks active scissor lifts. */}
      {(equipment.length > 0 || utilities.length > 0) && (
        <CollapsibleSection title={t('monitor.workforce.sections.equipment')}>
          <ul className={styles.list}>
            {equipment.map((eq) => (
              <li key={`eq-${eq.type}`} className={styles.row}>
                <img
                  className={styles.equipmentChipIcon}
                  src={getEquipmentIconFromType(eq.type)}
                  alt=""
                />
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>{eq.type}</span>
                </div>
                <span className={styles.rowMeta}>{eq.count}</span>
              </li>
            ))}
            {utilities.map((u) => (
              <li key={`util-${u.type}`} className={styles.row}>
                <img
                  className={styles.equipmentChipIcon}
                  src={getEquipmentIconFromType(u.type)}
                  alt=""
                />
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>{u.type}</span>
                </div>
                {u.hasLive && <span className={styles.liveDot} aria-hidden="true" />}
                <span className={styles.rowMeta}>{u.count}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}
    </div>
  );
}

interface ICollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
}

/** A Personnel-tab section with a chevron header that collapses its rows.
 *  Open by default; mirrors the collapsible cert group in SafetyComplianceCard. */
function CollapsibleSection({ title, children }: ICollapsibleSectionProps) {
  const [open, setOpen] = useState(true);
  return (
    <section>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden>
          <ChevronRightIcon size={12} />
        </span>
        <span className={styles.sectionTitle}>{title}</span>
      </button>
      {open && children}
    </section>
  );
}

interface IOpenRowProps {
  onOpen: () => void;
  children: ReactNode;
}

/** An activity's or issue's row: the whole row opens the item's drawer. */
function OpenRow({ onOpen, children }: IOpenRowProps) {
  return (
    <li>
      <button type="button" className={`${styles.row} ${styles.rowButton}`} onClick={onOpen}>
        {children}
      </button>
    </li>
  );
}

interface IActivitiesPanelProps {
  rows: { id: string; name: string; trade: string; color: string; onOpen: () => void }[];
  emptyText: string;
}

export function ActivitiesPanel({ rows, emptyText }: IActivitiesPanelProps) {
  if (rows.length === 0) return <EmptyMsg text={emptyText} />;
  return (
    <ul className={styles.list}>
      {rows.map((row) => (
        <OpenRow key={row.id} onOpen={row.onOpen}>
          <span className={styles.rowStripe} style={{ background: row.color }} />
          <span className={styles.rowMain}>
            <span className={styles.rowTitle}>{row.name}</span>
            <span className={styles.rowSub}>{row.trade}</span>
          </span>
        </OpenRow>
      ))}
    </ul>
  );
}

interface IIssuesPanelProps {
  rows: { id: string; title: string; owner: string; onOpen: () => void }[];
  loading: boolean;
  error: boolean;
  emptyText: string;
  loadingText: string;
  errorText: string;
}

export function IssuesPanel({
  rows,
  loading,
  error,
  emptyText,
  loadingText,
  errorText,
}: IIssuesPanelProps) {
  if (loading) return <EmptyMsg text={loadingText} />;
  if (error) return <EmptyMsg text={errorText} />;
  if (rows.length === 0) return <EmptyMsg text={emptyText} />;
  return (
    <ul className={styles.list}>
      {rows.map((row) => (
        <OpenRow key={row.id} onOpen={row.onOpen}>
          <span className={styles.rowMain}>
            <span className={styles.rowTitle}>{row.title}</span>
            <span className={styles.rowSub}>{row.owner}</span>
          </span>
        </OpenRow>
      ))}
    </ul>
  );
}

interface ISafetyPanelProps {
  rows: { id: string; type: string; isCritical: boolean; ts: string }[];
  emptyText: string;
}

export function SafetyPanel({ rows, emptyText }: ISafetyPanelProps) {
  if (rows.length === 0) return <EmptyMsg text={emptyText} />;
  return (
    <ul className={styles.list}>
      {rows.map((row) => (
        <li key={row.id} className={styles.row}>
          <div className={styles.rowMain}>
            <span className={styles.rowTitle}>{row.type}</span>
            <span className={styles.rowSub}>{row.ts}</span>
          </div>
          {row.isCritical && (
            <span className={`${styles.severityChip} ${styles.severityCritical}`}>!</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function EmptyMsg({ text }: { text: string }) {
  return <div className={styles.emptyMsg}>{text}</div>;
}
